/* eslint-disable max-len */
import { Col, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useState } from 'react';
import { ILeiPrevod } from 'types/leiTypes';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { IGliefData, checkGLeifApi, leiPrevodGet, leiPrevodZalozeni } from 'services/lei/leiTransfer';

import getLanguageObject from 'utils/getLanguageObject';
import leiValidation from 'utils/validations/leiValidation';

interface IZadaniLei {
    nextStep?: () => void;
    isPrehled?: boolean;
    setFormData?: React.Dispatch<React.SetStateAction<ILeiPrevod | undefined>>;
}

const ZadaniLei: React.FC<IZadaniLei> = ({ nextStep, isPrehled, setFormData }) => {
    const { cisStaty, language, lei } = useContext(Context);
    const { confirm } = Modal;
    const { setLevel } = useProfily();
    const [form] = Form.useForm();
    const [leiError, setLeiError] = useState<string>();
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);
    const [leiData, setLeiData] = useState<IGliefData>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        setLeiCheckLoading(true);
        checkGLeifApi(value.legalentityidentifier)
            .then(resp => {
                if (resp?.chybovystav !== 'OK') {
                    setLeiError('LEI_STAV_GLEIF_' + resp?.chybovystav);
                } else {
                    setLeiData(resp);
                    setLeiError('');
                }
            })
            .catch(() => setLeiError('LEI_CHECK_ERROR'))
            .finally(() => setLeiCheckLoading(false));
    };

    return (
        <LoadingWrapper loading={leiCheckLoading}>
            <FormWrapper outForm={form} onFinish={onFinish} layoutPosition={leiData ? 'horizontal' : 'vertical'}>
                {!leiData ? (
                    <>
                        <Title level={5} text="LEI_PREVOD_ZADAJTE_LEI" />

                        <InputComponent
                            label="LEI_CELE"
                            name="legalentityidentifier"
                            maxLength={20}
                            type="text"
                            rules={leiValidation()}
                        />
                    </>
                ) : (
                    <>
                        <Title
                            level={5}
                            text={isPrehled ? 'LEI_PREVOD_ZKONTROLUJTE_UDAJE_PREHLED' : 'LEI_PREVOD_ZKONTROLUJTE_UDAJE'}
                            dividerTitle={true}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="KOD_LEI"
                            name="legalentityidentifier"
                            initialValue={leiData.legalentityidentifier}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="ICO_SPOLECNOSTI"
                            name="registrationauthorityentityid"
                            initialValue={leiData.registrationauthorityentityid}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="EMP_PODANE_ZADOSTI_NAZEV_SPOLECNOSTI"
                            name="legalname"
                            initialValue={leiData.legalname}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="STAV_LEI"
                            name="registrationstatus"
                            initialValue={getLanguageObject(language)['LEI_GLEIF_STAV_' + leiData.registrationstatus]}
                        />
                        <SelectComponent
                            notRequired={true}
                            disabled={true}
                            isFormItem={true}
                            name="jurisdikce"
                            defaultValue={leiData.jurisdikce}
                            labelParagraph="LEI_DETAIL_JURISDICKE"
                            data={cisStaty}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="DATUM_VYDANI"
                            name="initialregistrationdate"
                            initialValue={dayjs(leiData.initialregistrationdate).format('DD.MM.YYYY')}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="DATUM_POSLEDNI_ZMENY"
                            name="lastupdatedate"
                            initialValue={dayjs(leiData.lastupdatedate).format('DD.MM.YYYY')}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="DATUM_EXPIRACE"
                            name="nextrenewaldate"
                            initialValue={dayjs(leiData.nextrenewaldate).format('DD.MM.YYYY')}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="SPRAVCE_LOU"
                            name="managinglou"
                            initialValue={leiData.managinglou}
                        />
                    </>
                )}
                {!isPrehled && (
                    <>
                        <Row>
                            {leiData && (
                                <Col>
                                    <ButtonComponent
                                        onClick={() => {
                                            setLeiData(undefined);
                                            setLeiError('');
                                        }}
                                        buttontext="ZPET"
                                        id="osobaZpetBtn"
                                        type="default"
                                        style={{
                                            marginRight: 10
                                        }}
                                    />
                                </Col>
                            )}
                            <Col>
                                <ButtonComponent
                                    buttontext={leiData ? 'LEI_PREVOD_BTN' : 'POKRACOVAT'}
                                    htmlType="button"
                                    disabled={!!leiError && !!leiData}
                                    onClick={() => {
                                        if (leiData) {
                                            confirm({
                                                title: <TranslateText>LEI_PREVOD</TranslateText>,
                                                content: (
                                                    <TranslateText>
                                                        {leiData.registrationstatus === 'LAPSED'
                                                            ? 'LEI_PREVOD_LAPSED'
                                                            : dayjs(leiData.nextrenewaldate).diff(
                                                                    dayjs(),
                                                                    'week',
                                                                    true
                                                                ) < 6
                                                              ? 'LEI_PREVOD_ISSUED_DUE'
                                                              : 'LEI_PREVOD_ISSUED'}
                                                    </TranslateText>
                                                ),
                                                cancelText: <TranslateText>ZAVRIT</TranslateText>,
                                                okText: (
                                                    <TranslateText>
                                                        {leiData.registrationstatus === 'LAPSED'
                                                            ? 'LEI_PREVOD_BTN_LAPSED'
                                                            : dayjs(leiData.nextrenewaldate).diff(
                                                                    dayjs(),
                                                                    'week',
                                                                    true
                                                                ) < 6
                                                              ? 'LEI_PREVOD_BTN_ISSUED_DUE'
                                                              : 'LEI_PREVOD_BTN_ISSUED'}
                                                    </TranslateText>
                                                ),
                                                onOk() {
                                                    setLeiCheckLoading(true);
                                                    leiPrevodZalozeni(leiData.legalentityidentifier)
                                                        .then(resp => {
                                                            leiPrevodGet(resp.varsym, resp.id)
                                                                .then(dataPrevod => {
                                                                    setLevel('lei', {
                                                                        ...lei,
                                                                        prevodData: dataPrevod,
                                                                        idVarsymZadosti: {
                                                                            id: resp.id,
                                                                            varsym: resp.varsym
                                                                        }
                                                                    });
                                                                    setFormData?.(dataPrevod);
                                                                    nextStep?.();
                                                                })
                                                                .finally(() => setLeiCheckLoading(false));
                                                        })
                                                        .catch(() => setLeiCheckLoading(false));
                                                }
                                            });
                                        } else {
                                            form.submit();
                                        }
                                    }}
                                    id="pokracovatBtn"
                                />
                            </Col>
                        </Row>
                        {leiError && (
                            <>
                                <Paragraph
                                    text={leiError}
                                    className="extramessage-text"
                                    style={{
                                        marginRight: 6
                                    }}
                                />
                                {leiError === 'LEI_STAV_GLEIF_UNAUTHJURISD' && (
                                    <AnchorComponent
                                        href="https://www.gleif.org/en/about-lei/code-lists/gleif-accepted-legal-jurisdictions-code-list"
                                        target="_blank"
                                    >
                                        GLEIF Accepted Legal Jurisdictions Code List
                                    </AnchorComponent>
                                )}
                            </>
                        )}
                    </>
                )}
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default ZadaniLei;
